import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronDown,
  faFolder,
  faFolderOpen,
  faChartBar,
  faUsers,
  faCog,
  faBuilding,
  faUserCog
} from '@fortawesome/free-solid-svg-icons';

const ReportsSidebar = ({ 
  reportsData, 
  selectedCategory, 
  selectedReport, 
  onCategorySelect, 
  onReportSelect 
}) => {
  const [expandedCategories, setExpandedCategories] = React.useState(
    selectedCategory ? [selectedCategory] : []
  );

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'system': return faCog;
      case 'business': return faBuilding;
      case 'custom': return faUserCog;
      default: return faFolder;
    }
  };

  const getCategoryDisplayName = (category) => {
    switch (category) {
      case 'system': return 'System Reports';
      case 'business': return 'Business Reports';
      case 'custom': return 'Custom Reports';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const getReportIcon = (report) => {
    const iconMap = {
      'users': faUsers,
      'tools': faCog,
      'chart-bar': faChartBar,
      'file-alt': faChartBar
    };
    return iconMap[report.icon] || faChartBar;
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {Object.entries(reportsData).map(([category, reports]) => {
        const isExpanded = expandedCategories.includes(category);
        const isSelected = selectedCategory === category;
        
        return (
          <div key={category} className="mb-2">
            {/* Category Header */}
            <div
              className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''
              }`}
              onClick={() => {
                toggleCategory(category);
                onCategorySelect(category);
              }}
            >
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon 
                  icon={isExpanded ? faFolderOpen : faFolder} 
                  className={`text-sm ${isSelected ? 'text-blue-500' : 'text-gray-500 dark:text-gray-400'}`} 
                />
                <span className={`font-medium text-sm ${
                  isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {getCategoryDisplayName(category)}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  isSelected 
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200' 
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {reports.length}
                </span>
              </div>
              <FontAwesomeIcon 
                icon={isExpanded ? faChevronDown : faChevronRight} 
                className={`text-xs transition-transform ${
                  isSelected ? 'text-blue-500' : 'text-gray-400'
                }`}
              />
            </div>

            {/* Reports List */}
            {isExpanded && (
              <div className="ml-6 border-l border-gray-200 dark:border-gray-600">
                {reports.length === 0 ? (
                  <div className="py-2 pl-4 text-sm text-gray-500 dark:text-gray-400 italic">
                    No reports in this category
                  </div>
                ) : (
                  reports.map((report) => {
                    const isReportSelected = selectedReport === report.name;
                    
                    return (
                      <div
                        key={report.name}
                        className={`flex items-center space-x-3 py-2 pl-4 pr-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                          isReportSelected ? 'bg-blue-50 dark:bg-blue-900/30 border-r-2 border-blue-500' : ''
                        }`}
                        onClick={() => onReportSelect(category, report.name)}
                      >
                        <FontAwesomeIcon 
                          icon={getReportIcon(report)} 
                          className={`text-xs ${
                            isReportSelected ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'
                          }`} 
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${
                            isReportSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {report.displayName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {report.description}
                          </div>
                          {report.tags && report.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {report.tags.slice(0, 3).map(tag => (
                                <span
                                  key={tag}
                                  className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                              {report.tags.length > 3 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{report.tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
      
      {/* Empty State */}
      {Object.keys(reportsData).length === 0 && (
        <div className="p-6 text-center">
          <FontAwesomeIcon icon={faChartBar} className="text-4xl text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 mb-2">No reports found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Try adjusting your search or filters
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportsSidebar;